/**
 * The PAY BASIS of a compensation record (ADR 0015, amending ADR 0011 decision 9).
 *
 * An employee's pay is SPECIFIED as one of three figures, and the payroll engine solves the other two:
 *
 *   - `ctc`    the annual cost to company (what every compensation record held before ADR 0015);
 *   - `gross`  the monthly gross the structure must add up to;
 *   - `net`    the monthly take-home.
 *
 * EXACTLY ONE AMOUNT MATCHES THE BASIS. A `gross` record carries a monthly gross and nothing else; a `net`
 * record a monthly net; a `ctc` record its annual CTC (which may be absent, as it is today for a basic-only or
 * hourly record) and neither monthly figure. The database enforces the same rule with a CHECK, so a row that
 * disagrees with itself cannot be written by any path.
 *
 * Pure and dependency-free (RN-safe): the contract, the API, the facts loader and the tests share it.
 */

import { z } from 'zod';

export const PAY_BASES = ['ctc', 'gross', 'net'] as const;
export type PayBasis = (typeof PAY_BASES)[number];
export const payBasisSchema = z.enum(PAY_BASES);

/** A record with no recorded basis is a CTC record: every row written before ADR 0015 is one. */
export const DEFAULT_PAY_BASIS: PayBasis = PAY_BASES[0];

/**
 * A stored compensation's basis. The column is a CHECKed text, so an unknown value cannot be written; if one ever
 * appeared it is read as CTC rather than trusted (and its amount would then be a missing fact, never a guess).
 */
export function payBasisOf(stored: string | null | undefined): PayBasis {
  const parsed = payBasisSchema.safeParse(stored);
  return parsed.success ? parsed.data : DEFAULT_PAY_BASIS;
}

/**
 * The named code the legacy calculation and the shadow comparison report for an employee whose pay is not specified
 * as CTC. They only understand CTC templates, so they FAIL CLOSED with this code instead of computing a wrong or empty
 * result. The payroll engine is the only path that calculates a gross or net basis.
 */
export const PAY_BASIS_UNSUPPORTED_BY_LEGACY = 'pay_basis_unsupported_by_legacy';

/** The amounts a compensation record may carry, as decimal strings (or null/absent). */
export interface PayBasisAmounts {
  readonly annualCtc?: string | null | undefined;
  readonly monthlyGross?: string | null | undefined;
  readonly monthlyNet?: string | null | undefined;
}

const present = (v: string | null | undefined): boolean => v !== null && v !== undefined;

/**
 * What is wrong with a basis and its amounts, as field-level messages (empty when it is fine).
 *
 * `gross` and `net` need their amount and refuse every other. `ctc` refuses the two monthly amounts and, when
 * `requireCtcAmount` is set (a NEW revision), needs the annual CTC. A stored `ctc` row may lack an annual CTC
 * (a basic-only record); that is a missing FACT at calculation time, not an inconsistent record.
 */
export function payBasisAmountProblems(
  basis: PayBasis,
  amounts: PayBasisAmounts,
  options: { readonly requireCtcAmount?: boolean } = {},
): Array<{ field: 'annualCtc' | 'monthlyGross' | 'monthlyNet'; message: string }> {
  const problems: Array<{ field: 'annualCtc' | 'monthlyGross' | 'monthlyNet'; message: string }> =
    [];
  const fields = ['annualCtc', 'monthlyGross', 'monthlyNet'] as const;
  const matching = { ctc: 'annualCtc', gross: 'monthlyGross', net: 'monthlyNet' } as const;
  const needed = matching[basis];
  for (const field of fields) {
    if (field === needed) {
      if (!present(amounts[field]) && (basis !== 'ctc' || options.requireCtcAmount)) {
        problems.push({ field, message: `Required when the pay basis is ${basis}` });
      }
    } else if (present(amounts[field])) {
      problems.push({
        field,
        message: `Not allowed when the pay basis is ${basis}: only ${needed} applies`,
      });
    }
  }
  return problems;
}
